<template>
  <div class="general-settings">
    <v-card class="settings-card" elevation="0">
      <!-- Main Content -->
      <div v-if="$store.state.loading" class="text-center pa-10">
        <v-progress-circular indeterminate color="primary"></v-progress-circular>
        <div class="mt-4">Loading settings...</div>
      </div>
      <div v-else class="settings-content">
        <!-- Coffee Processor Output Lot ID Method -->
        <div class="section mb-6">
          <h2 class="section-title mb-2">Coffee Processor Output Lot ID Method</h2>
          <h3 class="section-subtitle mb-6">
            Define how IDs are assigned to new lots created during the Quality Grading process.
          </h3>

          <!-- Green Beans Lot ID Type -->
          <div class="lot-id-section mb-8">
            <h4 class="lot-id-section-title mb-4">Select Lot ID Type for Green Beans</h4>
            <v-radio-group v-model="coffeeProcessorSettings.greenBeansLotIdType" class="lot-id-radio-group">
              <v-radio
                value="system"
                label="System Generated ID"
                class="lot-id-radio"
              >
                <template v-slot:label>
                  <span class="lot-id-label">System Generated ID</span>
                  <v-tooltip right>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        small
                        color="primary"
                        class="ml-2 info-icon"
                        v-bind="attrs"
                        v-on="on"
                      >
                        mdi-information
                      </v-icon>
                    </template>
                    <span>System will automatically generate the lot ID</span>
                  </v-tooltip>
                </template>
              </v-radio>
              <v-radio
                value="manual"
                label="Manual Lot ID (Outturn Number)"
                class="lot-id-radio"
              >
                <template v-slot:label>
                  <span class="lot-id-label">Manual Lot ID (Outturn Number)</span>
                  <v-tooltip right>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        small
                        color="primary"
                        class="ml-2 info-icon"
                        v-bind="attrs"
                        v-on="on"
                      >
                        mdi-information
                      </v-icon>
                    </template>
                    <span>User will manually enter the outturn number as the lot ID</span>
                  </v-tooltip>
                </template>
              </v-radio>
            </v-radio-group>
          </div>

          <!-- Quality Grading Lot ID Type -->
          <div class="lot-id-section">
            <h4 class="lot-id-section-title mb-4">Select Lot ID Type for Green Beans Quality Grades</h4>
            <v-radio-group v-model="coffeeProcessorSettings.qualityGradingLotIdType" class="lot-id-radio-group">
              <v-radio
                value="system"
                label="System Generated ID"
                class="lot-id-radio"
              >
                <template v-slot:label>
                  <span class="lot-id-label">System Generated ID</span>
                  <v-tooltip right>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        small
                        color="primary"
                        class="ml-2 info-icon"
                        v-bind="attrs"
                        v-on="on"
                      >
                        mdi-information
                      </v-icon>
                    </template>
                    <span>System will automatically generate the lot ID for quality grades</span>
                  </v-tooltip>
                </template>
              </v-radio>
              <v-radio
                value="manual"
                label="Manual Lot ID"
                class="lot-id-radio"
              >
                <template v-slot:label>
                  <span class="lot-id-label">Manual Lot ID</span>
                  <v-tooltip right>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                        small
                        color="primary"
                        class="ml-2 info-icon"
                        v-bind="attrs"
                        v-on="on"
                      >
                        mdi-information
                      </v-icon>
                    </template>
                    <span>User will manually enter the lot ID for quality grades</span>
                  </v-tooltip>
                </template>
              </v-radio>
            </v-radio-group>
          </div>
        </div>

        <!-- Save and Cancel Buttons -->
        <div class="d-flex justify-end mt-6">
          <v-btn
            class="text-none mr-3"
            height="40"
            color="primary"
            outlined
            @click="handleBack"
          >
            <span>{{ $t('cancel') }}</span>
          </v-btn>
          <v-btn
            class="primary text-none"
            height="40"
            @click="handleSave"
            :loading="saving"
          >
            <span>{{ $t('save') }}</span>
          </v-btn>
        </div>
      </div>
    </v-card>
  </div>
</template>

<script>
import CoffeeService from '@/_services/CoffeeService';
import loadingMixin from "@/mixins/LoadingMixin";

export default {
  name: 'GeneralSettings',
  mixins: [loadingMixin],
  data() {
    return {
      saving: false,
      coffeeProcessorSettings: {
        greenBeansLotIdType: 'system',
        qualityGradingLotIdType: 'system',
      },
    };
  },
  async mounted() {
    this.startLoading();
    try {
      await this.fetchCoffeeProcessorSettings();
    } catch (error) {
      console.error('Error loading settings:', error);
      this.$notify({
        title: 'Failed to load settings',
        type: 'error',
      });
    } finally {
      this.stopLoading();
    }
  },
  methods: {
    handleBack() {
      this.$router.go(-1);
    },
    async fetchCoffeeProcessorSettings() {
      try {
        const response = await CoffeeService.getCoffeeProcessorSettings();
        if (response.success && response.data) {
          this.coffeeProcessorSettings = {
            greenBeansLotIdType: response.data.greenBeansLotIdType || 'system',
            qualityGradingLotIdType: response.data.qualityGradingLotIdType || 'system',
          };
        }
      } catch (error) {
        console.error('Error fetching coffee processor settings:', error);
        // Set defaults if fetch fails
        this.coffeeProcessorSettings = {
          greenBeansLotIdType: 'system',
          qualityGradingLotIdType: 'system',
        };
      }
    },
    async handleSave() {
      this.saving = true;
      try {
        const response = await CoffeeService.updateCoffeeProcessorSettings({
          greenBeansLotIdType: this.coffeeProcessorSettings.greenBeansLotIdType,
          qualityGradingLotIdType: this.coffeeProcessorSettings.qualityGradingLotIdType,
        });
        
        if (response.success) {
          this.$notify({
            title: 'Settings saved successfully',
            type: 'success',
          });
        } else {
          throw new Error('Failed to save settings');
        }
      } catch (error) {
        console.error('Error saving settings:', error);
        this.$notify({
          title: 'Failed to save settings',
          text: error.message || 'An error occurred while saving settings',
          type: 'error',
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.general-settings {
  .settings-card {
    background-color: #ffffff;
    border-radius: 4px;
    padding: 24px;
  }

  .settings-content {
    max-width: 100%;
  }

  .section-title {
    font-size: 18px;
    font-weight: 600;
    color: #1e1e1e;
  }

  .section-subtitle {
    font-size: 14px;
    font-weight: 500;
    color: #787878;
  }

  .green-button {
    background-color: #00BD73 !important;
    color: white !important;
    border: none !important;
    
    &:hover {
      background-color: #00a65a !important;
    }
  }

  ::v-deep .v-select {
    .v-input__control {
      min-height: 40px;
    }
  }

  ::v-deep .v-text-field {
    .v-input__control {
      min-height: 40px;
    }
  }

  .lot-id-section {
    margin-bottom: 32px;
  }

  .lot-id-section-title {
    font-size: 16px;
    font-weight: 600;
    color: #1e1e1e;
  }

  .lot-id-radio-group {
    margin-top: 0;
  }

  .lot-id-radio {
    margin-bottom: 12px;
  }

  .lot-id-label {
    font-size: 14px;
    color: #1e1e1e;
  }

  .info-icon {
    cursor: help;
    vertical-align: middle;
  }

  ::v-deep .v-radio {
    .v-input--selection-controls__input {
      margin-right: 12px;
    }
  }
}
</style>
